

import React, { useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref, set, push, serverTimestamp } from 'firebase/database';
import { Save, X } from 'lucide-react';

const AddUnitForm = ({ onClose, onUnitAdded, currentUser, editUnit }) => {
  const [unitName, setUnitName] = useState(editUnit?.name || '');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  const handleSave = async () => {
    if (!unitName.trim()) {
      setMessage('Unit name cannot be empty!');
      return;
    }

    if (!currentUser) {
      setMessage('You must be logged in to add a unit');
      return;
    }

    setIsSubmitting(true);
    const encodedEmail = encodeEmailForPath(currentUser.email);

    try {
      if (editUnit) {
        // Edit existing unit
        const unitRef = ref(database, `users/${encodedEmail}/units/${editUnit.id}`);
        await set(unitRef, {
          name: unitName,
          createdAt: editUnit.createdAt,
          createdBy: currentUser.email
        });
        setMessage('Unit updated successfully!');
      } else {
        // Add new unit
        const newUnitRef = push(ref(database, `users/${encodedEmail}/units`));
        await set(newUnitRef, {
          name: unitName,
          createdAt: serverTimestamp(),
          createdBy: currentUser.email
        });
        setMessage('Unit saved successfully!');
      }

      setTimeout(() => {
        onUnitAdded();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error saving unit: ', error);
      setMessage('Error saving unit: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerStyle = {
    maxWidth: '100%',
    margin: '0 auto',
    padding: '2rem',
    backgroundColor: '#f8fafc',
    minHeight: '100vh',
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e2e8f0'
  };

  const headingStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#2563eb',
    margin: '0'
  };

  const buttonStyle = {
    padding: '0.875rem 2rem',
    borderRadius: '8px',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
    fontSize: '0.95rem',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
    transform: 'translateY(0)',
  };

  const tableContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0'
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem',
              borderRadius: '0.5rem',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              display: 'flex',
              alignItems: 'center'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            title="Back to Units"
          >
            <svg style={{ width: '1.5rem', height: '1.5rem', color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 style={headingStyle}>
              {editUnit ? 'Edit Unit' : 'Add New Unit'}
            </h1>
          </div>
        </div>
      </div>

      <div style={tableContainerStyle}>
        <div style={{
          padding: '2rem'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#2563eb',
            marginBottom: '1rem'
          }}>
            Unit Information
          </h2>
          
          <div style={{ maxWidth: '32rem' }}>
            <div style={{ marginBottom: '2rem' }}>
              <label style={{
                display: 'block',
                color: '#374151',
                fontWeight: 'bold',
                fontSize: '1.125rem',
                marginBottom: '0.75rem'
              }}>
                Unit Name *
              </label>
              <input
                type="text"
                value={unitName}
                onChange={(e) => setUnitName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  fontSize: '1.125rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '0.75rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                placeholder="Enter unit name (e.g., kg, liter, piece)"
              />
              <p style={{
                color: '#6b7280',
                fontSize: '0.875rem',
                marginTop: '0.5rem',
                margin: '0.5rem 0 0 0'
              }}>
                Enter the name of the unit of measurement
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleSave}
                disabled={isSubmitting}
                style={{
                  ...buttonStyle,
                  backgroundColor: '#16a34a',
                  boxShadow: '0 4px 15px rgba(22, 163, 74, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  opacity: isSubmitting ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(22, 163, 74, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(22, 163, 74, 0.3)';
                }}
              >
                <Save size={16} />
                {editUnit ? 'Update Unit' : 'Save Unit'}
              </button>
              <button
                onClick={onClose}
                disabled={isSubmitting}
                style={{
                  ...buttonStyle,
                  backgroundColor: '#6b7280',
                  boxShadow: '0 4px 15px rgba(107, 114, 128, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  opacity: isSubmitting ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(107, 114, 128, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(107, 114, 128, 0.3)';
                }}
              >
                <X size={16} />
                Cancel
              </button>
            </div>
            
            {message && (
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                borderRadius: '0.75rem',
                fontWeight: '500',
                fontSize: '1rem',
                backgroundColor: message.includes('Error') ? '#fef2f2' : '#f0fdf4',
                color: message.includes('Error') ? '#b91c1c' : '#166534',
                border: message.includes('Error') ? '2px solid #fecaca' : '2px solid #bbf7d0'
              }}>
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUnitForm;